import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import FeatureRenameDialog from "@/v2-backoffice-client/feature/rename/FeatureRenameDialog"
import LayerCategoryActionMenu from "@/v2-backoffice-client/layer/category/LayerCategoryActionMenu"
import { useLayerCategoryQuery } from "@/v2-backoffice-client/layer/category/layer.category.query"
import type { LayerCategoryFragment } from "@/v2-backoffice-client/layer/category/__types__/layer.category.fragment"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

const COLUMN_SIZING = {
  Priority: 80,
  IsEnabled: 80,
  LayerCount: 80,
}

const columns = createColumns<LayerCategoryFragment>({
  enableSelection: true,
  enableSorting: true,
  enableHiding: true,
  columns: {
    Priority: { id: "Priority", label: "Priority" },
    Name: {
      id: "Name",
      label: "Name",
      to: ({ row }) => `?v=layerCategory:${row.original.FeatureCode}`,
    },
    Description: {
      id: "Description",
      label: "Description",
      defaultHidden: true,
      renderCell({ row }) {
        const desc = row.original.Description
        if (!desc) return null
        return (
          <span className="truncate max-w-[200px]" title={desc}>
            {desc}
          </span>
        )
      },
    },
    FeatureCode: {
      id: "FeatureCode",
      label: "Feature Code",
      filter: { type: "text", placeholder: "Filter by code..." },
    },
    IsEnabled: {
      id: "IsEnabled",
      label: "Status",
      renderCell({ value }) {
        return (
          <DotText variant={value ? "success-1" : "destructive"}>
            {value ? "Enabled" : "Disabled"}
          </DotText>
        )
      },
      exportValue({ value }) {
        return value ? "Enabled" : "Disabled"
      },
    },
    LayerCount: {
      id: "LayerCount",
      label: "Layers",
    },
  },
})

export type LayerCategoryTableProps = Omit<
  TableProps<LayerCategoryFragment>,
  "data" | "columns"
>

function LayerCategoryTable(props: LayerCategoryTableProps) {
  const { className, ...tableProps } = props

  const opener = useAppViewSheetOpener()
  const { data, previousData, loading, error, refetch } = useLayerCategoryQuery(
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const displayData = data ?? previousData
  const categories = displayData?.getLayerCategories ?? []

  // The rename dialog lives up here because the row toolbar that opens it
  // only exists while its row is hovered — a dialog mounted inside it would
  // unmount the moment the pointer leaves for the dialog.
  const [renameTarget, setRenameTarget] =
    useState<LayerCategoryFragment | null>(null)

  return (
    <>
      <Table
        className={cn("layer-category-table", className)}
        tableId="layer-category-table"
        data={categories}
        columns={columns}
        initialColumnSizing={COLUMN_SIZING}
        enableRowSelection
        getHighlightedRowId={(row) => {
          return opener.getIsActive(
            "layerCategory",
            String(row.original.FeatureCode),
          )
        }}
        {...tableProps}
      >
        <Table.Toolbar>
          <div className="flex-1" />
          <Table.Search column="Name" placeholder="Search categories..." />
          <Separator orientation="vertical" />
          <Table.ToolbarMenu
            onRefresh={refetch}
            exportFileName="layer-categories"
          />
        </Table.Toolbar>

        <Table.FilterList />

        <Table.RowToolbar<LayerCategoryFragment> column="Name">
          {(category) => (
            <LayerCategoryActionMenu
              onRename={() => setRenameTarget(category)}
            />
          )}
        </Table.RowToolbar>

        <Table.Content
          countLabel="categories"
          isLoading={loading}
          error={error}
        />

        <Table.SelectionToolbar>
          {(categories) => (
            <Table.ExportSelectionButton
              fileName="layer-categories"
              selectionCount={categories.length}
            />
          )}
        </Table.SelectionToolbar>
      </Table>

      {renameTarget ? (
        <FeatureRenameDialog
          featureCode={renameTarget.FeatureCode}
          name={renameTarget.Name}
          open
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null)
          }}
        />
      ) : null}
    </>
  )
}

export default LayerCategoryTable
